import { ActionTakenType } from '../../../api-client/autogenerated';
import { CitationInfo } from '../core/types';
import { ActionOption } from './review-actions';
import { SUBMITTAL_HEADINGS } from './review-prompts';
import { ReviewKind } from './review-visibility';

/**
 * Reading a review's answer (plan §5.2): the Architect's proposed response, the recommended action,
 * and the answer as plain text with human references in place of `[n]`, because the contractor
 * must never see a marker that means nothing outside CENTERLINE AI.
 */

type Citations = Partial<CitationInfo>[] | null | undefined;

const CITE_GROUP = /\[(\d+(?:\s*,\s*\d+)*)\](?!\()/g;

/** `(Spec 22 11 16, 3.5.B)`, `(Sheet A-501)` or `(<file>, p. 3)`; null when nothing identifies it. */
export function referenceFor(info: Partial<CitationInfo> | undefined): string | null {
  if (!info) return null;
  if (info.clauseId) return `Spec ${info.clauseId.split('/').join(', ')}`;
  if (info.sheetNumber) return `Sheet ${info.sheetNumber}`;
  if (info.fileName) return info.page ? `${info.fileName}, p. ${info.page}` : info.fileName;
  return null;
}

const byNumber = (citations: Citations, n: number) => (citations ?? []).find((c) => c.n === n);

/** Every `[n]` or `[n, m]` becomes `(ref)` or `(ref; ref)`; a number nothing identifies is dropped. */
export function replaceCitationMarkers(text: string, citations: Citations): string {
  return text
    .replace(CITE_GROUP, (_, group: string) => {
      const refs: string[] = [];
      group.split(',').forEach((part) => {
        const ref = referenceFor(byNumber(citations, Number(part.trim())));
        if (ref && !refs.includes(ref)) refs.push(ref);
      });
      return refs.length ? `(${refs.join('; ')})` : '';
    })
    .replace(/[ \t]+([.,;:])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ');
}

/** Markdown to text a person can paste into a form: no markers, lists and numbering kept. */
export function markdownToPlainText(markdown: string): string {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  lines.forEach((raw) => {
    const t = raw.trimEnd();
    if (/^\s*```/.test(t)) return;
    if (/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(t) && t.includes('-')) return;
    let line = t.replace(/^\s{0,3}#{1,6}\s+/, '');
    if (line.includes('|') && /^\s*\|.*\|\s*$/.test(line)) {
      line = line
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => c.trim())
        .join(' | ');
    }
    line = line
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '$1 ($2)')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/(^|[^*])\*([^*\s](?:[^*]*?[^*\s])?)\*/g, '$1$2')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^(\s*)[*+]\s+/, '$1- ');
    out.push(line);
  });
  return out
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export interface ReviewSection {
  heading: string;
  body: string;
}

const HEADING_LINE = /^\s{0,3}#{1,3}\s+(.+?)\s*#*\s*$/;

/** The answer split at its headings; text before the first heading is a section with no heading. */
export function splitSections(answer: string): ReviewSection[] {
  const sections: ReviewSection[] = [];
  let current: { heading: string; lines: string[] } = { heading: '', lines: [] };
  const flush = () => {
    const body = current.lines.join('\n').trim();
    if (current.heading || body) sections.push({ heading: current.heading, body });
  };
  answer
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((line) => {
      const m = HEADING_LINE.exec(line);
      if (m) {
        flush();
        current = { heading: m[1].replace(/\*\*/g, '').trim(), lines: [] };
      } else {
        current.lines.push(line);
      }
    });
  flush();
  return sections;
}

const norm = (s: string) =>
  ` ${s
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `;

/** A heading is the one asked for when it starts with the same two words ("Proposed response …"). */
const headingKey = (s: string) => norm(s).trim().split(' ').slice(0, 2).join(' ');

const findSection = (sections: ReviewSection[], heading: string) =>
  sections.find((s) => s.heading && headingKey(s.heading) === headingKey(heading));

/** The words the standard actions have when the subscriber has not renamed them. */
const DEFAULT_LABELS: Partial<Record<ActionTakenType, string>> = {
  [ActionTakenType.NoExceptionsTaken]: 'No Exceptions Taken',
  [ActionTakenType.AmendAsNoted]: 'Amend As Noted',
  [ActionTakenType.ReviseAndResubmit]: 'Revise & Resubmit',
  [ActionTakenType.Rejected]: 'Rejected',
  [ActionTakenType.SubmitSpecifiedItem]: 'Submit Specified Item',
};

/**
 * The action a "Recommended action" section names, from its first line: the earliest of the
 * subscriber's wordings (or the standard ones) that appears there. Undefined when none does, or the
 * action is not among `options` (the subscriber switched it off).
 */
export function matchAction(body: string, options: ActionOption[]): ActionOption | undefined {
  const firstLine = norm(
    body
      .split('\n')
      .map((l) => l.trim())
      .find(Boolean) ?? '',
  );
  let best: { option: ActionOption; at: number; length: number } | undefined;
  options.forEach((option) => {
    [option.label, DEFAULT_LABELS[option.action]].forEach((words) => {
      if (!words) return;
      const needle = norm(words);
      const at = firstLine.indexOf(needle);
      if (at < 0) return;
      if (!best || at < best.at || (at === best.at && needle.length > best.length)) {
        best = { option, at, length: needle.length };
      }
    });
  });
  return best?.option;
}

export interface ParsedReview {
  sections: ReviewSection[];
  /** Plain text for the response field, with human references. */
  proposedResponse: string;
  recommendedAction?: ActionOption;
  /** The answer had the "Proposed response" heading; otherwise `proposedResponse` is all of it. */
  structured: boolean;
}

export function parseReview(
  answer: string,
  citations: Citations,
  kind: ReviewKind,
  actions: ActionOption[],
): ParsedReview {
  const sections = splitSections(answer);
  const response = findSection(sections, SUBMITTAL_HEADINGS.response);
  const convert = (md: string) => markdownToPlainText(replaceCitationMarkers(md, citations));
  const recommended =
    kind === 'submittal' ? findSection(sections, SUBMITTAL_HEADINGS.action) : undefined;
  const found = response && response.body.trim();
  return {
    sections,
    proposedResponse: convert(found ? response!.body : answer),
    recommendedAction: found && recommended ? matchAction(recommended.body, actions) : undefined,
    structured: !!found,
  };
}

/** The numbers of the citations the answer's text refers to, in order of first use. */
export function citedNumbers(answer: string): number[] {
  const seen: number[] = [];
  for (const m of answer.matchAll(CITE_GROUP)) {
    m[1].split(',').forEach((p) => {
      const n = Number(p.trim());
      if (!seen.includes(n)) seen.push(n);
    });
  }
  return seen;
}

/** One line per cited source: its reference and, if different, the file it is in. */
export function sourceLines(answer: string, citations: Citations): string[] {
  return citedNumbers(answer).flatMap((n) => {
    const info = byNumber(citations, n);
    const ref = referenceFor(info);
    if (!info || !ref) return [];
    const file = info.fileName && info.fileName !== ref ? ` — ${info.fileName}` : '';
    const page = info.page && info.fileName && !ref.includes('p. ') ? `, p. ${info.page}` : '';
    return [`${ref}${file}${page}`];
  });
}

/** What Copy puts on the clipboard: the answer as plain text, then a Sources list. */
export function copyText(answer: string, citations: Citations): string {
  const body = markdownToPlainText(replaceCitationMarkers(answer, citations));
  const sources = Array.from(new Set(sourceLines(answer, citations)));
  return sources.length ? `${body}\n\nSources\n${sources.map((s) => `- ${s}`).join('\n')}` : body;
}
